import { useCallback } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { AnalyticEvent, AnalyticsEvents } from '@sweatpals/shared';
import { useAmplitudeContext } from '../../contexts';

/**
 * Hook for using Amplitude analytics within the application.
 *
 * @returns An object containing methods to interact with Amplitude analytics
 *
 * @example
 * ```tsx
 * const { logEventTyped } = useAmplitudeAnalytics();
 *
 * // Log an analytics event
 * logEventTyped('user_signup', { user_id: '123', method: 'email' });
 * ```
 *
 * @note This hook must be used within the AmplitudeContext provider scope
 */
export const useAmplitudeAnalytics = () => {
	const { isInitializedRef, setUserIdExternally } = useAmplitudeContext();

	const logEventTyped = useCallback(
		<T extends AnalyticsEvents>(
			event: T,
			metadata: AnalyticEvent<T>,
			amplitudeOptions?: amplitude.Types.EventOptions
		) => {
			try {
				if (!isInitializedRef?.current) {
					console.warn('[Amplitude] Amplitude is not initialized yet!');
				}

				metadata = {
					data_stream: 'landing',
					...metadata,
				};

				amplitude.track(event, metadata, amplitudeOptions);
				console.info('[Amplitude] Event logged: ', { event, metadata });
			} catch (e) {
				console.error('[Amplitude] Error while logging analytical event', e);
			}
		}, [isInitializedRef, isInitializedRef?.current]
	);

	return {
		logEventTyped, setUserIdExternally
	};
};
